import {
  AxisUpdateVerdictSchema,
  ClaimKindSchema,
  ClaimStrengthSchema,
  OpenPointTypeSchema,
} from "@shared/labs/level-assessment-round-update";

/**
 * Client-side twin of the server's `rounds/voice-gate.ts` `scrubEngineTokens`
 * (`docs/labs/level-assessment/multi-round-narrative-layer.md` §5 ⟨R2⟩ — "scrub
 * covers BOTH fallback surfaces"). The server module can't be imported from
 * the client bundle, so this duplicates the same STRUCTURAL, digit-anchored
 * regexes (never a hand-listed word blocklist) against the same zod enums —
 * both sides stay in lockstep because both derive the enum-literal check from
 * the schemas, not from a copied list.
 *
 * Used ONLY on the report's axis-detail fallback path (`assessment.reasoning`
 * rendered when no composed `explanation` exists) — a HISTORICAL evaluation
 * that predates the S1 narrative layer. Never applied to fresh
 * server-synthesized text, which already cleared the voice gate before being
 * persisted.
 */

const ID_TWO_SEGMENT = /\b(cl|op)-\d+-\d+\b/i;
const ID_SINGLE_SEGMENT_LOWERCASE = /\b(cl|op)-\d+\b/;

function enumOptions(schema: { options: readonly string[] }): readonly string[] {
  return schema.options;
}

const ENUM_TOKENS: readonly string[] = [
  ...enumOptions(AxisUpdateVerdictSchema),
  ...enumOptions(ClaimKindSchema),
  ...enumOptions(ClaimStrengthSchema),
  ...enumOptions(OpenPointTypeSchema),
];

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const ENUM_TOKEN_PATTERN = ENUM_TOKENS.map(escapeRegExp).join("|");
const ENUM_TOKEN_REGEX = new RegExp(`\\b(?:${ENUM_TOKEN_PATTERN})\\b`, "i");

/** Legit pt-BR prose never contains an underscore-joined lowercase run. */
const SNAKE_CASE_REGEX = /\b[a-z]+_[a-z_]+\b/;

/** Best-effort scrub for a historical display-only fallback field: strips
 *  every offending substring (engine ids, raw zod-enum literals, snake_case
 *  keys) and collapses whitespace. Never throws; returns `text` unchanged
 *  when it is empty. Digit-anchored, so "CL-500 (o cliente)" passes untouched. */
export function scrubEngineTokensClient(text: string): string {
  if (!text) return text;
  let out = text;
  out = out.replace(new RegExp(ID_TWO_SEGMENT.source, "gi"), "");
  out = out.replace(new RegExp(ID_SINGLE_SEGMENT_LOWERCASE.source, "g"), "");
  out = out.replace(new RegExp(ENUM_TOKEN_REGEX.source, "gi"), "");
  out = out.replace(new RegExp(SNAKE_CASE_REGEX.source, "g"), "");
  return out.replace(/\s+/g, " ").trim();
}
